// src/features/system/orchestrator/components/OrchestratorManagement.tsx
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Tabs from '@/shared/components/ui/Tabs';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { fetchWorkersApi, Worker } from '../api/workersApi';
import IncomesV2Panel from './IncomesV2Panel';
import OutcomesRegistryPanelV2 from './OutcomesRegistryPanelV2';
import WorkersV2Panel from './WorkersV2Panel';
import HandlerRegistryPanelV2 from './HandlerRegistryPanelV2';
import WorkflowDataCleanupPanel from './WorkflowDataCleanupPanel';
import ScopeCenterPanel from './ScopeCenterPanel';
import EdgeTasksPanel from '@/features/system/edge-tasks/components/EdgeTasksPanel';
import EdgeMetricsPanel from '@/features/system/edge-tasks/components/EdgeMetricsPanel';
import WorkflowManagementAccordionV2 from './WorkflowManagementAccordionV2';

const QUERY_KEY = ['admin', 'orchestrator', 'workers'] as const;

type Section = 'workers' | 'handlers' | 'incomes' | 'outcomes' | 'workflow' | 'cleanup' | 'scope-center' | 'edge-tasks' | 'edge-metrics';

const SECTION_TABS: Array<{ key: Section; label: string }> = [
  { key: 'workers', label: 'Workers' },
  { key: 'handlers', label: 'Handler Catalog' },
  { key: 'incomes', label: 'Incomes v2' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'workflow', label: 'Workflow' },
  { key: 'cleanup', label: 'Cleanup' },
  { key: 'scope-center', label: 'Scope Center' },
  { key: 'edge-tasks', label: 'Edge Tasks' },
  { key: 'edge-metrics', label: 'Edge Metrics' },
];

const SECTION_COPY: Record<Section, { title: string; blurb: string }> = {
  workers: {
    title: 'Workers',
    blurb: 'Registry view of orchestrator workers.',
  },
  handlers: {
    title: 'Handler Catalog',
    blurb: 'Register handlers and manage versioned entries.',
  },
  incomes: {
    title: 'Incomes v2',
    blurb: 'Scope-based income definitions (workflow.income namespace). Replaces legacy input_definitions.',
  },
  outcomes: {
    title: 'Outcomes',
    blurb: 'Grouped view: global + run overlays, plus handler-scoped definitions by handler key base.',
  },
  workflow: {
    title: 'Workflow',
    blurb: 'Workflow builder wired to the handler catalog (outcomes-projected handlers).',
  },
  cleanup: {
    title: 'Cleanup',
    blurb: 'Safely preview and purge workflow lifecycle data by filters.',
  },
  'scope-center': {
    title: 'Scope Center',
    blurb: 'Centralized scope management — migration, cleanup, and KPI analytics.',
  },
  'edge-tasks': {
    title: 'Edge Tasks',
    blurb: 'Dispatch lightweight tasks to PACDaemon edge agents and monitor their lifecycle.',
  },
  'edge-metrics': {
    title: 'Edge Metrics',
    blurb: 'Collect and visualize system metrics (CPU, memory, disk, network) from PACDaemon edge agents via HotStream.',
  },
};

export default function OrchestratorManagement() {
  const [section, setSection] = React.useState<Section>('workflow');
  const [sp] = useSearchParams();
  const sectionLabel = SECTION_TABS.find(t => t.key === section)?.label ?? '';
  useDocumentTitle('Admin', 'Orchestrator', sectionLabel);

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('orchTab');
    if (!v || v === sectionLabel) return;
    const found = SECTION_TABS.find(t => t.label === v);
    if (found && found.key !== section) setSection(found.key);
  }, [sp]);

  const { data: workers = [], isLoading, error } = useQuery<Worker[], Error>({
    queryKey: [...QUERY_KEY],
    queryFn: () => fetchWorkersApi({}),
  });

  const headerCopy = SECTION_COPY[section];

  return (
    <section className="h-full min-h-0 flex flex-col gap-4 overflow-hidden">
      <div className="space-y-3 shrink-0">
        <div className="max-w-full overflow-x-auto">
          <Tabs
            tabs={SECTION_TABS.map((t) => t.label)}
            activeTab={SECTION_TABS.find((t) => t.key === section)?.label ?? SECTION_TABS[0].label}
            urlKey="orchTab"
            urlParams={{ adminTab: 'Orchestrator Management' }}
            onTabChange={(label) => {
              const found = SECTION_TABS.find((t) => t.label === label);
              if (found) setSection(found.key);
            }}
          />
        </div>
        {(headerCopy.title || headerCopy.blurb) && (
          <div>
            {headerCopy.title && (
              <h2 className="text-lg font-semibold tracking-tight">{headerCopy.title}</h2>
            )}
            {headerCopy.blurb && (
              <p className="text-xs text-neutral-500">{headerCopy.blurb}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {section === 'workers' ? (
          <WorkersV2Panel />
        ) : section === 'handlers' ? (
          <HandlerRegistryPanelV2 />
        ) : section === 'incomes' ? (
          <IncomesV2Panel />
        ) : section === 'outcomes' ? (
          <OutcomesRegistryPanelV2 />
        ) : section === 'cleanup' ? (
          <WorkflowDataCleanupPanel />
        ) : section === 'scope-center' ? (
          <ScopeCenterPanel />
        ) : section === 'edge-tasks' ? (
          <EdgeTasksPanel />
        ) : section === 'edge-metrics' ? (
          <EdgeMetricsPanel />
        ) : (
          <div className="h-full min-h-0 flex flex-col overflow-hidden">
            <WorkflowManagementAccordionV2
              workers={workers}
              workersLoading={isLoading}
              workersError={error ?? null}
            />
          </div>
        )}
      </div>
    </section>
  );
}
